<template>
  <Component
    :is="canEditTags ? 'button' : 'div'"
    v-if="tags.length"
    v-bind="editAttributes"
    class="table-tags-cell"
    :class="{ 'table-tags-cell--editable': canEditTags }"
  >
    <div class="table-tags-cell__visible">
      <BittsTag
        v-for="tag in displayedTags"
        :key="tag.id"
        :text="tag.label"
        size="x-small"
        variant="rounded"
      />
    </div>
    <BittsTooltip
      v-if="hiddenCount > 0"
      placement="bottomLeft"
      :mount-to-body="true"
      :mouse-enter-delay="0.3"
    >
      <span class="table-tags-cell__overflow"> +{{ hiddenCount }} </span>
      <template #title>
        {{ joinLabels(hiddenTags) }}
      </template>
    </BittsTooltip>
    <FontAwesomeIcon
      v-if="canEditTags"
      :icon="['fak', 'edit']"
      class="table-tags-cell__edit-icon"
    />
    <div v-if="hiddenCount > 0" class="table-tags-cell__gradient" />
  </Component>
  <button
    v-else-if="canEditTags"
    type="button"
    class="table-tags-cell__add"
    @click="editTags"
  >
    Add Tags
  </button>
  <span v-else class="text-sm text-neutral-text-weak">&ndash;</span>
</template>

<script setup lang="ts">
import { BittsTag, BittsTooltip } from '@crossbeam/bitts';

import { ICellRendererParams } from '@ag-grid-community/core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

import type { TableCellTag } from './types';

const { params } = defineProps<{
  params: ICellRendererParams;
}>();

const TAG_PADDING = 24;
const CHAR_WIDTH = 6.3;
const GAP = 4;
const OVERFLOW_BADGE_WIDTH = 30;
const EDIT_ICON_WIDTH = 10;

const tags = computed<TableCellTag[]>(() => params.value ?? []);

const canEditTags = computed<boolean>(
  () => params.context?.canEditTags?.() ?? false,
);

function joinLabels(items: TableCellTag[]) {
  return items.map((tag) => tag.label).join(', ');
}

function editTags() {
  params.context?.editTags?.(params.data?.uuid);
}

const editAttributes = computed(() =>
  canEditTags.value
    ? {
        type: 'button',
        'aria-label': `Edit tags: ${joinLabels(tags.value)}`,
        onClick: editTags,
      }
    : {},
);

const minWidth = params.column?.getColDef()?.minWidth;
const DEFAULT_COLUMN_WIDTH = minWidth ?? 200;
const columnWidth = ref(DEFAULT_COLUMN_WIDTH);

function getTagWidth(tag: TableCellTag) {
  return tag.label.length * CHAR_WIDTH + TAG_PADDING;
}

function fitTags(items: TableCellTag[], availableWidth: number) {
  let currentWidth = 0;
  const visible: TableCellTag[] = [];

  for (const tag of items) {
    const width = getTagWidth(tag) + (visible.length ? GAP : 0);
    if (currentWidth + width > availableWidth && visible.length) break;
    currentWidth += width;
    visible.push(tag);
  }

  return visible;
}

const tagsWidth = computed(() =>
  canEditTags.value
    ? columnWidth.value - EDIT_ICON_WIDTH - GAP
    : columnWidth.value,
);

const displayedTags = computed(() => {
  const allFit = fitTags(tags.value, tagsWidth.value);
  if (allFit.length === tags.value.length) return allFit;
  return fitTags(tags.value, tagsWidth.value - OVERFLOW_BADGE_WIDTH);
});

const hiddenTags = computed(() => tags.value.slice(displayedTags.value.length));
const hiddenCount = computed(() => hiddenTags.value.length);

function onColumnResized() {
  columnWidth.value = params.column?.getActualWidth() ?? DEFAULT_COLUMN_WIDTH;
}

onMounted(async () => {
  await nextTick();
  columnWidth.value = params.column?.getActualWidth() ?? DEFAULT_COLUMN_WIDTH;
  params.api.addEventListener('columnResized', onColumnResized);
});

onBeforeUnmount(() => {
  if (params.api.isDestroyed()) return;
  params.api.removeEventListener('columnResized', onColumnResized);
});
</script>

<style lang="pcss">
@reference "../../../css/theme.css";
.table-tags-cell {
  @apply relative flex items-center gap-4 h-full w-full cursor-default;
}
.table-tags-cell--editable {
  @apply cursor-pointer;
}
.table-tags-cell__edit-icon {
  @apply h-10 w-10 shrink-0 text-neutral-text-weak opacity-0 transition-opacity relative z-20;

  .table-tags-cell--editable:hover &,
  .table-tags-cell--editable:focus-visible & {
    @apply opacity-100;
  }
}
.table-tags-cell__add {
  @apply cursor-pointer text-sm text-secondary-text hover:text-secondary-accent;
}
.table-tags-cell__visible {
  @apply flex items-center gap-4 overflow-hidden;
}
.table-tags-cell__overflow {
  @apply text-sm text-neutral-text shrink-0 relative z-20;
}
.table-tags-cell__gradient {
  @apply absolute right-0 top-0 w-[40px] h-full z-10 pointer-events-none;
  background: linear-gradient(
    270deg,
    theme(colors.white) 40%,
    theme(colors.white / 0) 100%
  );
}
.ag-row-hover .table-tags-cell__gradient {
  background: linear-gradient(
    270deg,
    theme(colors.neutral-background-weak) 40%,
    theme(colors.neutral-background-weak / 0) 100%
  );
}
</style>
